import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useData } from '../../context/DataContext';
import { useAuth } from '../../context/AuthContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiSave, FiSearch, FiUser, FiCheck, FiPlus, FiTrash2 } = FiIcons;

const TaskModal = ({ task, onClose, initialData = {} }) => {
  const { createTask, updateTask, teamMembers, clients, projects } = useData();
  const { user } = useAuth();
  
  // State for form fields
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    status: 'pending',
    assignedTo: [], 
    clientId: '',
    projectId: '',
    deadline: ''
  });

  const [memberSearchTerm, setMemberSearchTerm] = useState('');

  // Smart Body Scroll Lock
  useEffect(() => {
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, []);

  // Initialize Data - CHANGED: Empty dependency array ensures this runs ONLY ONCE when mounted
  useEffect(() => {
    if (task) {
      let assignedToArray = [];
      if (task.assignedTo) {
        assignedToArray = Array.isArray(task.assignedTo) ? task.assignedTo : [task.assignedTo];
      }
      setFormData({
        title: task.title || '',
        description: task.description || '',
        priority: task.priority || 'medium',
        status: task.status || 'pending',
        assignedTo: assignedToArray.map(String),
        clientId: task.clientId || '',
        projectId: task.projectId || '',
        deadline: task.deadline ? new Date(task.deadline).toISOString().split('T')[0] : ''
      });
    } else {
      // Create Mode
      setFormData(prev => ({
        ...prev,
        ...initialData,
        assignedTo: [],
        projectId: initialData.projectId || prev.projectId,
        status: 'pending'
      }));
    }
  }, []); // <--- CRITICAL FIX: Removed dependencies to prevent reset on re-renders

  const handleSubmit = (e) => {
    e.preventDefault();
    const taskData = {
      ...formData,
      clientId: formData.clientId || null,
      projectId: formData.projectId || null,
      deadline: new Date(formData.deadline).toISOString(),
      assignedBy: (task && task.assignedBy) ? task.assignedBy : user.id
    };

    if (task) {
      updateTask(task.id, taskData);
    } else {
      createTask(taskData);
    }
    onClose();
  };

  // Toggle member selection
  const toggleMember = (memberId) => {
    const idStr = String(memberId);
    setFormData(prev => {
      const current = prev.assignedTo;
      if (current.includes(idStr)) {
        return { ...prev, assignedTo: current.filter(id => id !== idStr) };
      } else {
        return { ...prev, assignedTo: [...current, idStr] };
      }
    });
  };

  // Derived lists
  const availableMembers = teamMembers.filter(member => 
    member.name.toLowerCase().includes(memberSearchTerm.toLowerCase()) ||
    member.role.toLowerCase().includes(memberSearchTerm.toLowerCase())
  );

  const selectedMembersObj = formData.assignedTo.map(id => 
    teamMembers.find(m => String(m.id) === id)
  ).filter(Boolean);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4 backdrop-blur-sm"
      onClick={(e) => {
        // Close only if clicking the backdrop directly
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 bg-gray-50/50">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              {task ? 'Edit Task' : 'Create New Task'}
            </h2>
            <p className="text-sm text-gray-500 mt-1">Fill in the details below</p>
          </div>
          <button 
            type="button"
            onClick={onClose} 
            className="p-2 hover:bg-red-50 hover:text-red-500 rounded-lg transition-colors text-gray-400"
          >
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable Form Content */}
        <form id="task-form" onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
          
          {/* Main Info */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Task Title</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder="What needs to be done?"
                required
                autoFocus
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                placeholder="Add details about this task..."
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Priority</label>
              <div className="relative">
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white appearance-none"
                >
                  <option value="low">Low Priority</option>
                  <option value="medium">Medium Priority</option>
                  <option value="high">High Priority</option>
                </select>
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none text-gray-500">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Deadline</label>
              <input
                type="date"
                value={formData.deadline}
                onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>

          {/* New Assignment Interface */}
          <div className="bg-gray-50 p-5 rounded-xl border border-gray-200">
            <label className="block text-sm font-bold text-gray-800 mb-3 flex items-center">
              <SafeIcon icon={FiUser} className="w-4 h-4 mr-2 text-blue-600" />
              Assign Team Members
            </label>

            {/* Currently Selected */}
            {selectedMembersObj.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {selectedMembersObj.map(member => (
                  <div 
                    key={member.id}
                    className="flex items-center bg-white pl-1 pr-3 py-1 rounded-full border border-blue-200 shadow-sm"
                  >
                    <img 
                      src={member.avatar || 'https://via.placeholder.com/32'} 
                      alt="" 
                      className="w-6 h-6 rounded-full mr-2 object-cover bg-gray-100" 
                    />
                    <span className="text-sm font-medium text-gray-800 mr-2">{member.name}</span>
                    <button 
                      type="button"
                      onClick={() => toggleMember(member.id)}
                      className="text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <SafeIcon icon={FiX} className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Selection Area */}
            <div className="bg-white rounded-lg border border-gray-300 overflow-hidden">
              {/* Search Header */}
              <div className="relative border-b border-gray-200 bg-gray-50/50">
                <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search members to add..."
                  className="w-full pl-9 pr-4 py-2.5 bg-transparent border-none focus:ring-0 text-sm"
                  value={memberSearchTerm}
                  onChange={(e) => setMemberSearchTerm(e.target.value)}
                />
              </div>

              {/* Scrollable List */}
              <div className="max-h-48 overflow-y-auto">
                {availableMembers.length > 0 ? (
                  <div className="divide-y divide-gray-100">
                    {availableMembers.map(member => {
                      const isSelected = formData.assignedTo.includes(String(member.id));
                      return (
                        <div 
                          key={member.id}
                          onClick={() => toggleMember(member.id)}
                          className={`flex items-center px-4 py-3 cursor-pointer transition-colors hover:bg-gray-50 ${isSelected ? 'bg-blue-50/50' : ''}`}
                        >
                          <div className={`w-5 h-5 rounded border flex items-center justify-center mr-3 transition-colors ${isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300 bg-white'}`}>
                            {isSelected && <SafeIcon icon={FiCheck} className="w-3.5 h-3.5 text-white" />}
                          </div>
                          <img 
                            src={member.avatar || 'https://via.placeholder.com/32'} 
                            alt="" 
                            className="w-8 h-8 rounded-full mr-3 object-cover bg-gray-200" 
                          />
                          <div className="flex-1">
                            <p className={`text-sm font-medium ${isSelected ? 'text-blue-800' : 'text-gray-900'}`}>{member.name}</p>
                            <p className="text-xs text-gray-500 capitalize">{member.role?.replace('_', ' ')}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="p-8 text-center text-gray-500 text-sm">
                    No members found matching "{memberSearchTerm}"
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Project (Optional)</label>
              <select
                value={formData.projectId}
                onChange={(e) => setFormData({ ...formData, projectId: e.target.value })}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
                disabled={!!initialData.projectId}
              >
                <option value="">No project</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Client (Optional)</label>
              <select
                value={formData.clientId}
                onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 bg-white"
              >
                <option value="">No client</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50 flex justify-end space-x-3">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 text-gray-700 font-medium hover:bg-gray-200 rounded-lg transition-colors border border-gray-300 bg-white"
          >
            Cancel
          </button>
          <motion.button
            type="submit"
            form="task-form"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="px-6 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors shadow-lg shadow-blue-200 flex items-center space-x-2"
          >
            <SafeIcon icon={FiSave} className="w-4 h-4" />
            <span>{task ? 'Update Task' : 'Create Task'}</span>
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default TaskModal;